/**
 * Shared working-hours punch / timer helpers for Dashboard + HRMS Login.
 *
 * Rules:
 * - Completed login→logout pairs count toward worked time.
 * - If the user is still logged in on the same work-region calendar day, the live
 *   timer includes (now − last login).
 * - If they forgot to logout and the work day has ended (open login on a past
 *   day), that open segment counts as 0 — only completed pairs for that day.
 *
 * Work days use the employee's saved work timezone (India IST or US PST).
 * That preference is chosen once and reused on every punch until they change it.
 * Fallback: USER_DATA region/country, then browser zone, then Asia/Kolkata.
 */

import { getWorkCalendarTimeZone } from "./workCalendar";
import { getSavedWorkTimeZone } from "./workTimezonePreference";

export const MAX_LOGIN_CYCLES = 3;
export const MAX_PAIRS_FOR_VIEW = 3;

export const pad2 = (n) => String(n).padStart(2, "0");

/** IANA zone used for working-hours calendar day boundaries. */
export const getWorkingHoursTimeZone = () =>
  getSavedWorkTimeZone() || getWorkCalendarTimeZone();

/**
 * Calendar YYYY-MM-DD in the saved work timezone (not OS local).
 * Prefer this for punch work_date / "is today?" checks.
 */
export const getLocalYMD = (d = new Date()) => {
  const dt =
    d == null || d === "" ? new Date() : d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";

  const tz = getWorkingHoursTimeZone();
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(dt);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    if (!y || !m || !day) return "";
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
};

export const addDaysYmd = (ymd, days) => {
  const parts = String(ymd || "")
    .split("-")
    .map(Number);
  if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) {
    return getLocalYMD();
  }
  // Calendar arithmetic on Y-M-D parts (timezone-neutral).
  const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(
    dt.getUTCDate(),
  )}`;
};

export const isSameDay = (a, b) => getLocalYMD(a) === getLocalYMD(b);

/**
 * Whether punches are allowed right now.
 * A work day is a local calendar date — NOT a rolling 24h window from first login.
 * Always allow punches for the current calendar day (past forgotten sessions do not block).
 */
export const canPunchOnCalendarDay = (_row, _now = new Date()) => true;

export const isLoginEventType = (type) => {
  const t = String(type || "").toLowerCase();
  return t === "login" || t === "in" || t === "checkin" || t === "check_in";
};

export const isLogoutEventType = (type) => {
  const t = String(type || "").toLowerCase();
  return t === "logout" || t === "out" || t === "checkout" || t === "check_out";
};

export const normalizeEvents = (eventsRaw) => {
  const arr = Array.isArray(eventsRaw) ? eventsRaw : [];
  return arr
    .map((e) => ({
      type: e?.type || e?.event_type || e?.eventType || e?.action,
      at: e?.at || e?.event_at || e?.eventAt || e?.createdAt || e?.time,
      mode: e?.mode || e?.event_mode || e?.punch_mode || null,
      source: e?.source || e?.event_source || e?.punch_source || "manual",
    }))
    .filter((e) => e.type && e.at)
    .sort((a, b) => new Date(a.at) - new Date(b.at));
};

export const countLoginEvents = (events) =>
  (Array.isArray(events) ? events : []).filter((e) =>
    isLoginEventType(e?.type),
  ).length;

export const countCompletedLogoutCycles = (events) =>
  (Array.isArray(events) ? events : []).filter((e) =>
    isLogoutEventType(e?.type),
  ).length;

export const formatElapsedHMS = (ms) => {
  const totalSec = Math.max(0, Math.floor(Number(ms) / 1000));
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;
  return `${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
};

export const formatHHMM = (mins) => {
  const m = Math.max(0, Number(mins) || 0);
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${pad2(hh)}:${pad2(mm)} hrs`;
};

/** Keep punches that fall on this work-timezone calendar date. */
export const filterEventsForWorkDay = (events, workDateYmd) => {
  const day = String(workDateYmd || "").trim() || getLocalYMD();
  if (!day) return [];
  return (Array.isArray(events) ? events : []).filter((e) => {
    if (!e?.at) return false;
    return getLocalYMD(e.at) === day;
  });
};

export const resolveWorkDateYmd = (rowOrDate) => {
  if (rowOrDate == null) return "";
  if (typeof rowOrDate === "string" || typeof rowOrDate === "number") {
    const raw = String(rowOrDate);
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    return getLocalYMD(new Date(raw));
  }
  const d =
    rowOrDate?.date || rowOrDate?.work_date || rowOrDate?.workDate || null;
  if (!d) return "";
  const raw = String(d);
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return getLocalYMD(new Date(d));
};

/**
 * If API has first_login_at but empty events, treat as an open login
 * (only for the current work-region day — past days stay empty → 0).
 */
export const resolveRowEvents = (row) => {
  let events = normalizeEvents(
    row?.events || row?.working_events || row?.logs,
  );
  const firstLoginAt =
    row?.first_login_at ||
    row?.firstLoginAt ||
    row?.first_login ||
    row?.firstLogin;
  const totalMinutesNum =
    Number(row?.total_minutes ?? row?.totalMinutes ?? 0) || 0;
  const workYmd = resolveWorkDateYmd(row);
  const todayYmd = getLocalYMD();
  const isTodayRow = !workYmd || workYmd === todayYmd;

  if (
    (!events || events.length === 0) &&
    firstLoginAt &&
    totalMinutesNum === 0 &&
    isTodayRow
  ) {
    events = [{ type: "login", at: firstLoginAt }];
  }
  return events;
};

export const rowHasOpenLogin = (row) => {
  const events = resolveRowEvents(row);
  if (!events.length) return false;
  const last = events[events.length - 1];
  if (!isLoginEventType(last?.type)) return false;
  const workYmd = resolveWorkDateYmd(row);
  const todayYmd = getLocalYMD();
  // Past work-region day open login is considered closed/forgotten (0).
  if (workYmd && workYmd < todayYmd) return false;
  return true;
};

/**
 * Elapsed ms for timer + whether an active (today in work zone) login exists.
 * An open login after that day ends keeps time through midnight in the work zone.
 */
export const computeElapsedMsFromEvents = (
  events,
  nowMs = Date.now(),
  { workDateYmd } = {},
) => {
  const arr = Array.isArray(events) ? events : [];
  let elapsedMs = 0;
  let currentLoginAtMs = null;
  let lastCompletedLoginAtMs = null;
  let lastCompletedLogoutAtMs = null;

  for (const e of arr) {
    const atMs = new Date(e?.at).getTime();
    if (!Number.isFinite(atMs)) continue;

    if (isLoginEventType(e?.type)) {
      currentLoginAtMs = atMs;
    } else if (isLogoutEventType(e?.type)) {
      if (currentLoginAtMs != null && atMs > currentLoginAtMs) {
        elapsedMs += atMs - currentLoginAtMs;
        lastCompletedLoginAtMs = currentLoginAtMs;
        lastCompletedLogoutAtMs = atMs;
      }
      currentLoginAtMs = null;
    }
  }

  const todayYmd = getLocalYMD(new Date(nowMs));
  const workYmd = workDateYmd || "";

  // Open login stays live on the work-timezone day it started.
  // After that day ends, keep the time up to midnight in that zone (not 0).
  if (currentLoginAtMs != null) {
    const loginYmd = getLocalYMD(new Date(currentLoginAtMs));
    const openIsToday = loginYmd === todayYmd;
    if (openIsToday) {
      elapsedMs += Math.max(0, nowMs - currentLoginAtMs);
      return {
        elapsedMs: Math.max(0, elapsedMs),
        activeLoginAtMs: currentLoginAtMs,
        lastCompletedLoginAtMs,
        lastCompletedLogoutAtMs,
        hasOpenLoginForgotten: false,
      };
    }
    const dayEnd = startOfZonedDayUtcMs(
      addDaysYmd(loginYmd || workYmd, 1),
      getWorkingHoursTimeZone(),
    );
    const cap = dayEnd != null ? Math.min(nowMs, dayEnd) : nowMs;
    if (cap > currentLoginAtMs) elapsedMs += cap - currentLoginAtMs;
    return {
      elapsedMs: Math.max(0, elapsedMs),
      activeLoginAtMs: null,
      lastCompletedLoginAtMs,
      lastCompletedLogoutAtMs,
      hasOpenLoginForgotten: true,
    };
  }

  return {
    elapsedMs: Math.max(0, elapsedMs),
    activeLoginAtMs: null,
    lastCompletedLoginAtMs,
    lastCompletedLogoutAtMs,
    hasOpenLoginForgotten: false,
  };
};

/**
 * Day session clock: completed login→logout pairs plus the open session if any.
 * Logout does not reset it — the next login continues from the same total.
 * After the selected work timezone's calendar day ends, this is 00:00:00.
 */
function startOfZonedDayUtcMs(ymd, timeZone) {
  const match = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const tz = timeZone || getWorkingHoursTimeZone();
  let guess = Date.UTC(y, m - 1, d, 0, 0, 0);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  for (let i = 0; i < 4; i += 1) {
    const parts = fmt.formatToParts(new Date(guess));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
    const hour = get("hour") % 24;
    const got = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      hour,
      get("minute"),
      get("second"),
    );
    const want = Date.UTC(y, m - 1, d, 0, 0, 0);
    const next = guess + (want - got);
    if (next === guess) break;
    guess = next;
  }
  return guess;
}

export const computeSessionTimerFromEvents = (
  events,
  nowMs = Date.now(),
  { workDateYmd } = {},
) => {
  const todayYmd = getLocalYMD(new Date(nowMs));
  const result = computeElapsedMsFromEvents(events, nowMs, {
    workDateYmd: workDateYmd || todayYmd,
  });
  if (result.activeLoginAtMs) return result;
  const day = workDateYmd || todayYmd;
  if (day !== todayYmd) {
    return {
      elapsedMs: 0,
      activeLoginAtMs: null,
      lastCompletedLoginAtMs: null,
      lastCompletedLogoutAtMs: null,
      hasOpenLoginForgotten: false,
    };
  }
  return result;
};

/**
 * Minutes worked for TWH.
 * includeLiveOpenSession counts the open login through now, or through
 * midnight in the work timezone once that day has ended.
 */
export const computeMinutesWorked = (
  events,
  { workDateYmd, nowMs = Date.now(), includeLiveOpenSession = false } = {},
) => {
  const { elapsedMs } = computeElapsedMsFromEvents(events, nowMs, {
    workDateYmd,
  });
  if (!includeLiveOpenSession) {
    // Strip live open-session time for table TWH — only completed pairs.
    const arr = Array.isArray(events) ? events : [];
    let totalMs = 0;
    let currentLoginAtMs = null;
    for (const e of arr) {
      const atMs = new Date(e?.at).getTime();
      if (!Number.isFinite(atMs)) continue;
      if (isLoginEventType(e?.type)) {
        currentLoginAtMs = atMs;
      } else if (isLogoutEventType(e?.type)) {
        if (currentLoginAtMs != null && atMs > currentLoginAtMs) {
          totalMs += atMs - currentLoginAtMs;
        }
        currentLoginAtMs = null;
      }
    }
    return Math.round(totalMs / 60000);
  }
  return Math.round(elapsedMs / 60000);
};
